import { SCRIPT_IMG, ScriptImage } from "@/components/ScriptImage";

function SectionHeading({ imgSrc, imgAlt }) {
  return (
    <div className="text-center pt-2">
      <ScriptImage src={imgSrc} alt={imgAlt} />
    </div>
  );
}

function SubBlock({ title, children }) {
  return (
    <div className="space-y-1">
      <h4
        className="text-[#76232F] text-[17px] md:text-[18px] leading-tight"
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontWeight: 500,
          letterSpacing: "0.01em",
        }}
      >
        {title}
      </h4>
      <div
        className="text-[15px] md:text-base leading-[1.7] text-[#2C1A1D]/85 space-y-3"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {children}
      </div>
    </div>
  );
}

export default function TravelPanel() {
  return (
    <div
      data-testid="panel-block-travel"
      className="max-w-[680px] mx-auto px-2 md:px-0 pb-16"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      <SectionHeading imgSrc={SCRIPT_IMG.travel} imgAlt="Getting to The Dairy" />

      <p className="text-center text-[15px] md:text-base leading-[1.8] text-[#2C1A1D]/85 mt-8 mb-3">
        We visited one venue in this whole wedding journey and immediately, we
        knew the search was over. Surrounded by the heritage-listed Ravensbourne
        National Park, we are so excited to celebrate with you at one of the
        most breathtaking places we&rsquo;ve ever seen — The Dairy, Ravensbourne.
      </p>
      <p className="text-center italic text-[14px] md:text-[15px] text-[#76232F]/80 mb-8">
        70 Philp Road, Ravensbourne, Queensland
      </p>

      {/* Interactive Google map */}
      <div className="relative w-full overflow-hidden border border-[#76232F]/15 bg-[#F4EBDD] mb-14" style={{ aspectRatio: "4 / 3" }}>
        <iframe
          title="The Dairy Ravensbourne map"
          data-testid="travel-map"
          src="https://maps.google.com/maps?q=The%20Dairy%2C%20Ravensbourne%2C%2070%20Philp%20Rd%2C%20Ravensbourne%20QLD%204352%2C%20Australia&t=&z=13&ie=UTF8&iwloc=B&output=embed"
          className="absolute inset-0 w-full h-full"
          style={{ border: 0, filter: "grayscale(0.25) contrast(0.95)" }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      </div>
      <div className="text-center mb-14 -mt-10">
        <a
          href="https://www.google.com/maps/search/?api=1&query=The+Dairy+Ravensbourne+70+Philp+Rd+Ravensbourne+QLD+4352+Australia"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block text-[10px] uppercase tracking-[0.32em] text-[#76232F]/80 hover:text-[#76232F] transition-colors border-b border-[#76232F]/30 hover:border-[#76232F] pb-1"
          data-testid="travel-map-link"
        >
          Open in Google Maps
        </a>
      </div>

      <div className="space-y-12">
        <SubBlock title="By Air">
          <p>
            For guests travelling from interstate or overseas,{" "}
            <a
              href="https://www.bne.com.au/"
              target="_blank"
              rel="noopener noreferrer"
              className="italic text-[#76232F] hover:underline underline-offset-4 decoration-1 decoration-[#76232F]/50 transition-all"
            >
              Brisbane Airport (BNE)
            </a>{" "}
            offers the widest range of flight options. The drive from Brisbane
            Airport to The Dairy takes approximately 1 hour 45 minutes to 2
            hours through the scenic Brisbane Valley region.
          </p>
        </SubBlock>

        <SubBlock title="Car Hire &amp; Driving">
          <p>
            Hiring a car is the easiest way to explore the region and travel
            between The Dairy and your accommodation. Car rental services are
            available at Brisbane Airport:{" "}
            {[
              {
                name: "SIXT",
                url: "https://www.sixt.com.au/car-hire/australia/brisbane/brisbane-airport/",
              },
              {
                name: "Europcar",
                url: "https://www.europcar.com.au/en-au/branches/australia/brisbane/brisbane-airport",
              },
              {
                name: "Hertz",
                url: "https://www.hertz.com.au/p/car-hire/australia/brisbane/brisbane-airport",
              },
              {
                name: "Avis",
                url: "https://www.avis.com.au/en/locations/au/queensland/brisbane-airport/bne",
              },
              {
                name: "Budget",
                url: "https://www.budget.com.au/en/locations/au/qld/brisbane-airport",
              },
            ].map((c, i, arr) => (
              <span key={c.name}>
                <a
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="italic text-[#76232F] hover:underline underline-offset-4 decoration-1 decoration-[#76232F]/50 transition-all"
                >
                  {c.name}
                </a>
                {i < arr.length - 1 ? ", " : "."}
              </span>
            ))}
          </p>
          <div className="pt-2">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#76232F] mb-3">
              Approximate driving times
            </div>
            <ul className="space-y-2 pl-0">
              {[
                ["Brisbane Airport", "The Dairy", "1 hour 45 minutes – 2 hours"],
                ["Gold Coast", "The Dairy", "2 hours – 2 hours 30 minutes"],
                ["Toowoomba CBD", "The Dairy", "45 minutes"],
                ["Ravensbourne Town", "The Dairy", "5 minutes"],
              ].map(([from, to, time]) => (
                <li
                  key={from}
                  className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-1 border-b border-[#76232F]/10 pb-2"
                >
                  <span>
                    {from} <span className="text-[#76232F]/60">→</span> {to}
                  </span>
                  <span className="italic text-[#2C1A1D] text-[14px] md:text-[15px] sm:text-right">
                    {time}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </SubBlock>

        <SubBlock title="Taxi &amp; Rideshare">
          <p>
            Uber and taxi services are available in Toowoomba and Brisbane;
            however, availability can be limited in Ravensbourne, particularly
            late in the evening. We recommend arranging transport in advance
            where possible.
          </p>
        </SubBlock>

        <SubBlock title="Wedding Transport">
          <p>
            If you&rsquo;re staying with family or friends, we encourage
            carpooling where possible. If staying in Ravensbourne and Toowoomba,
            please let us know when you RSVP whether you require transport
            before and after the wedding.
          </p>
        </SubBlock>
      </div>
    </div>
  );
}
